/* eslint-disable react/jsx-no-bind */
/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {ClickAwayListener, Paper} from '@material-ui/core';
import Popper from '@material-ui/core/Popper';
import {makeStyles} from '@material-ui/core/styles';
import cloneDeep from 'lodash.clonedeep';
import debounce from 'lodash.debounce';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import isNull from 'lodash.isnull';
import moment from 'moment';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {
  safeJSONParse,
  safeJSONStringify
} from '@ion/core/src/utils/JSONUtils';
import {
  isDownArrow,
  isEnter,
  isTab
} from '@ion/core/src/utils/KeyEventUtils';
import {onlyMultipleNumbers, onlyNumbers} from '@ion/core/src/utils/NumberUtil';
import TextField from '@ion/dls/src/components/textfield/TextField';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {DeletePrompt} from '@ion/dls/src/editors/DeletePrompt';
import {InfoIcon} from '@ion/dls/src/icons/InfoIcon';
import {TopNavSearchIcon} from '@ion/dls/src/icons/TopNavSearchIcon';
import {Hidden} from '@ion/dls/src/layout/Hidden';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import Render from '@ion/dls/src/render/Render';
import {dlsFloatingCardStyle, toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsShadow} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {FilterChips} from '@ion/dls/src/types/editors';
import {DLSTheme, ViewSubGroupsPosition} from '@ion/dls/src/types/types';
import GroupInheritance from './groups/GroupInheritance';
import GroupSearchInput from './groups/GroupSearchInput';
import RoleSearchInput from './roles/RoleSearchInput';
import AttributeSuggestions from './AttributeSuggestions';
import BaseSearchMapping from './BaseSearchMappings';
import DisplayQueryChips from './DisplayQueryChips';
import {DisplayQueryChipsLayout} from './DisplayQueryChipsLayout';
import SearchFieldTooltip from './SearchFieldTooltip';
import SearchScope, {
  SearchScopeValues
} from './SearchScope';
import {SearchSuggestions} from './SearchSuggestions';
import {getSearchableDate} from './SearchUtils';
import {
  AccountIdLinkMap,
  AttributeTypes,
  copyPasteQueryNames,
  DateAttributes,
  DateLabels,
  Dropdown,
  LookUpData,
  LookUpSource,
  MenuItem,
  QueryChipData,
  SAVED_QUERIES_VALUE,
  SearchQuery,
  SuggestionsType
} from './types';
import {useQueryDialog} from './useQueryDialog';
import {
  filterDynamicDataList,
  filterOutExactOrPartialQueries,
  getDateSelection,
  getMappingToolTip,
  getQueryTypeForAttribute,
  getSavedQueriesForPage,
  getSelectedChipValue,
  hasFilteredExactMatch,
  hasWhitespaceOrComma,
  IDENTIFIER_MAX_IDENTIFIER_SEARCH_LIMIT,
  isAnySuggestionType,
  isExactSearchScope,
  isIdentifierLimitExceed,
  isQueryAndPageValid,
  isQueryAttributeValid,
  isSearchQueryValueType,
  isValidLogicalSearch,
  MAX_IDENTIFIER_SEARCH_LIMIT,
  PRIMARY_ID_SEARCH_LIMIT,
  removeEndSeperators,
  SavedQueryItem,
  SavedQueryType,
  SEARCH_QUERIES_PREFERENCE_NAME
} from './utils';

export enum SuggestionOptions {
  GROUP = 'groupIds'
}

enum PageName {
  MFG_DEVICES = 'manufacturedDevices'
}

const debouncedInputSelectionMap = {
  [SuggestionOptions.GROUP] : true
};

// current-theme search field width, shared by the input wrapper and its
// underline so the two can never drift out of sync ...
const CURRENT_THEME_SEARCH_FIELD_WIDTH = '308px';

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    // the wrapping div below exists purely so ClickAwayListener has a real
    // DOM node to attach its ref to (a bare Fragment can't receive one) -
    // current theme actually wants that fix (it repairs click-outside-to-
    // close), but legacy's box model shouldn't change just as a side
    // effect. display:contents makes this div's own box disappear from
    // layout entirely, so legacy renders exactly as if it were still a
    // Fragment, while current theme keeps a normal box + the ref fix ...
    clickAwayWrapper : toggleThemeStyle({
      display : 'contents'
    }, {}),
    widgetWrapper : {
      display                        : 'flex',
      flexDirection                  : 'column',
      marginLeft                     : toggleThemeStyle('0', '20px'),
      gap                            : toggleThemeStyle('10px', '8px'),
      height                         : toggleThemeStyle('100px', '60px'),
      justifyContent                 : toggleThemeStyle(undefined, 'center'),
      width                          : '60%',
      [theme.breakpoints.down('sm')] : {
        flexDirection  : 'row',
        alignItems     : 'center',
        justifyContent : 'space-between',
        height         : 'auto',
        width          : '100%',
        gap            : '25px',
        position       : 'relative',
        bottom         : '28px',
        padding        : '0 23px'
      }
    },
    widgetWrapperFullWidth : {
      display                        : 'flex',
      flexDirection                  : 'column',
      gap                            : '10px',
      height                         : '100px',
      width                          : '100%',
      [theme.breakpoints.down('sm')] : {
        height        : ({isGridInsideADialog}: {isGridInsideADialog: boolean}) => isGridInsideADialog ? 'auto' : '100px',
        flexDirection : ({isGridInsideADialog}: {isGridInsideADialog: boolean}) => isGridInsideADialog ? 'row' : 'column'
      }
    },
    searchWrapper : toggleThemeStyle({
      display       : 'flex',
      flexDirection : 'row',
      flexWrap      : 'nowrap',
      gap           : '12px'
    }, {
      gap                : '8px',
      display            : 'flex',
      flexWrap           : 'nowrap',
      flexDirection      : 'row',
      '& .MuiInput-root' : {
        gap             : '8px',
        width           : '228px !important',
        border          : `1px solid ${dlsColor('grey5')} !important`,
        height          : '44px !important',
        display         : 'flex',
        padding         : '8px 8px 8px 0 !important',
        borderRadius    : '8px !important',
        '&.Mui-focused' : {
          border : `1px solid ${dlsColor('darkTeal')} !important`
        }
      },
      '& .MuiInputBase-input' : {
        color         : `${dlsColor('grey1')} !important`,
        fontSize      : '14px !important',
        fontFamily    : 'Inter !important',
        fontWeight    : 500,
        lineHeight    : '16px !important',
        letterSpacing : '0 !important'
      },
      '& .MuiInput-input' : {
        paddingLeft : '18px !important'
      }
    }),
    phoneSearchWrapper : {
      position : 'relative',
      width    : '75%'
    },
    searchSuggestionInputWrapper : {
      maxWidth : ({
        isPhone,
        isCurrentTheme,
        isMobileModularSearch
      }: {
        isPhone: boolean,
        isCurrentTheme: boolean,
        isMobileModularSearch: boolean
      }) => (isPhone && isMobileModularSearch) ? '600px' : isCurrentTheme ? CURRENT_THEME_SEARCH_FIELD_WIDTH : '500px',
      width : ({
        isPhone,
        isCurrentTheme,
        isMobileModularSearch
      }: {
        isPhone: boolean,
        isCurrentTheme: boolean,
        isMobileModularSearch: boolean
      }) => (isPhone && isMobileModularSearch) ? '100%' : isCurrentTheme ? CURRENT_THEME_SEARCH_FIELD_WIDTH : '60%',
      position : 'relative'
    },
    searchSuggestionInputWrapperFullWidth : {
      width                          : '73%',
      position                       : 'relative',
      [theme.breakpoints.down('sm')] : {
        width : ({isGridInsideADialog}: {isGridInsideADialog: boolean}) => isGridInsideADialog ? '100%' : '73%'
      }
    },
    searchSuggestionInputRoot : toggleThemeStyle({
      height             : '48px',
      '& .MuiInput-root' : {
        minHeight : '48px'
      }
    }, {
      height             : '44px',
      '& .MuiInput-root' : {
        minHeight : '44px'
      }
    }),
    searchSuggestionInputUnderline : toggleThemeStyle({
      padding     : '12px !important',
      height      : 'inherit',
      '&::before' : {
        borderBottom : 'transparent'
      },
      '&:hover' : {
        '&::before' : {
          borderBottom : `2px solid ${theme.dls.colors.aaBlue} !important`
        }
      }
    }, {
      gap             : '8px',
      width           : `${CURRENT_THEME_SEARCH_FIELD_WIDTH} !important`,
      height          : '44px',
      display         : 'flex',
      padding         : '8px',
      borderRadius    : '8px',
      backgroundColor : dlsColor('white')
    }),
    suggestionsWrapper : {
      zIndex    : 5,
      top       : '-112px !important',
      transform : 'translate3d(0px, 160px, 0px) !important',
      left      : 'unset !important',
      right     : 'unset !important',
      // Dropdown.tsx's own Popper has no border at all - its Paper's own
      // elevation shadow is the whole visual boundary, so this Popper
      // drawing its own border on top of that read as a doubled edge.
      // current theme only - legacy keeps its own border ...
      border    : toggleThemeStyle('1px solid #e0e0e0', 'none'),
      maxWidth  : 500,
      width     : '100%'
    },
    // matches the current-theme "floating card" popup chrome used
    // elsewhere (e.g. SearchOperationsDropDown.tsx's own popoverRoot for
    // the Saved Search menu) - a full 1px border on all 4 sides plus 8px
    // rounded corners, rather than Dropdown.tsx's own bare paper (0px
    // radius, no border, elevation-only). Current theme only - legacy
    // already uses MUI's own default Paper look here. No marginTop (unlike
    // popoverRoot) - this Popper already positions itself via its own
    // top/transform above, so an extra marginTop just left a gap between
    // the Popper's edge and the Paper reading as a second line ...
    suggestionsPaper : toggleThemeStyle({}, {
      ...dlsFloatingCardStyle(),
      padding      : '0px',
      // the paper itself doesn't need to scroll - listWrapper (inside
      // SearchSuggestions) already scrolls on its own - this is purely so
      // the rounded corners actually clip the list's own square corners
      // instead of them poking out past the rounded edge ...
      overflow     : 'hidden',
      textOverflow : 'ellipsis'
    }),
    searchIcon : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    exactContainer : {
      marginRight : '5px'
    },
    copyPasteIcon : {
      '& span' : {
        display        : 'flex',
        justifyContent : 'center',
        alignItems     : 'center',
        cursor         : 'pointer'
      }
    },
    selectFormControl : {
      width                  : '300px',
      height                 : '48px',
      '& .MuiInputBase-root' : {
        width     : '100%',
        height    : '48px',
        marginTop : 0
      }
    },
    attributesSelect : {
      width      : toggleThemeStyle('30%', '100%'),
      minWidth   : 150,
      height     : '27px',
      display    : 'flex',
      alignItems : 'center',
      fontSize   : 14,
      fontFamily : theme.typography.fontFamily
    },
    attributesSelectionMenu : toggleThemeStyle({
      width     : '260px',
      maxHeight : 400,
      overflow  : 'scroll',
      overflowX : 'hidden',
      marginTop : 10,
      fontSize  : 14
    }, {
      width           : '228px',
      fontSize        : '14px',
      overflow        : 'scroll',
      marginTop       : '8px',
      maxHeight       : '336px',
      overflowX       : 'hidden',
      border          : `1px solid ${dlsColor('grey5')}`,
      borderRadius    : '8px',
      boxShadow       : dlsShadow(4),
      backgroundColor : `${dlsColor('white')} !important`
    }),
    attributesMenuItem : toggleThemeStyle({
      height     : '48px',
      marginLeft : '11px',
      fontSize   : 14,
      color      : dlsColor('grey3')
    }, {
      height     : '44px',
      padding    : '26px 16px !important',
      fontSize   : '14px',
      fontFamily : 'Inter',
      fontWeight : 400,
      color      : dlsColor('grey1'),
      '&:hover'  : {
        backgroundColor : dlsColor('paleBlue')
      },
      '&.Mui-selected' : {
        backgroundColor : `${dlsColor('paleBlue')} !important`,
        '&:hover'       : {
          backgroundColor : `${dlsColor('paleBlue2')} !important`
        }
      }
    }),
    input : toggleThemeStyle({
      fontSize   : 14,
      fontFamily : theme.typography.fontFamily
    }, {
      color         : dlsColor('grey3'),
      fontSize      : '14px',
      fontFamily    : 'Inter',
      fontWeight    : 400,
      lineHeight    : '16px',
      letterSpacing : '0'
    })
  };
});

export interface ModularSearchWidgetProps {
  pageName: string;
  searchInstance?: BaseSearchMapping;
  getSearchParams?: Function;
  getIdentifierParams?: (param: string) => void;
  inputPlaceholder?: string;
  getFilterQueries?: (filters: FilterChips) => void;
  autoSelectNext?: boolean;
  showFullWidthSearch?: boolean;
  isPhone?: boolean;
  isMobileModularSearch?: boolean;
  isCaseInsensitive?: boolean;
  hasDateField?: boolean;
  accountTree: AccountIdLinkMap;
  accountId?: number;
  viewSubGroups?: boolean;
  viewInheritableGroup?: boolean;
  toggleInheritableGroupView?: () => void
  hasInheritableGroups?: boolean;
  isGridInsideADialog?: boolean;
  action?: React.ReactNode;
  externalQueries?: QueryChipData[];
  onQueriesChange?: (queries: QueryChipData[]) => void;
}

/**
 * Constants for the component
 */
export const SEARCH_NAME = 'last search';

const SEARCH_INPUT_ID    = 'search-input';
const SEARCH_WRAPPER     = 'modular-search-wrapper';
const VBUS_STATUS        = 'vbusEnabledStatus';
const IS_THIRD_PARTY     = 'isThirdParty';
const TRUE               = 'true';
const FALSE              = 'false';
const VBUS_STATUS_ON     = 'On';
const VBUS_STATUS_OFF    = 'Off';
const IS_THIRD_PARTY_YES = 'YES';
const IS_THIRD_PARTY_NO  = 'NO';

const INITIAL_SEARCH_QUERY = {
  query  : '',
  option : null
};

export const removeTempQueries = (pageName: string) => {
  const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
  const savedQueriesLength = savedQueriesForPage?.query?.length;
  if (savedQueriesLength) {
    const filteredQueries = savedQueriesForPage?.query?.filter(({name}: SavedQueryItem) => name !== SEARCH_NAME);
    return {
      ...savedQueriesForPage,
      query : cloneDeep(filteredQueries)
    };
  }
  return savedQueriesForPage;
};

export const isEqualArrays = (array1: string[], array2: string[]) => {
  const array2Sorted = array2.slice().sort();
  return array1.length === array2.length && array1.slice().sort().every(function(value, index) {
    return value === array2Sorted[index];
  });
};

/**
 * It will display GlobalSearchWidget, here user can enter keyword
 * to search data in Accounts/Users/Assets/Devices/Groups
 */
export const ModularSearchWidget = ({
  getFilterQueries,
  pageName,
  searchInstance,
  getSearchParams,
  getIdentifierParams,
  toggleInheritableGroupView,
  inputPlaceholder,
  autoSelectNext,
  isPhone = false,
  viewInheritableGroup = false,
  isMobileModularSearch = false,
  hasInheritableGroups = false,
  showFullWidthSearch = false,
  isCaseInsensitive = false,
  hasDateField = false,
  viewSubGroups = false,
  isGridInsideADialog = false,
  accountTree = {},
  accountId,
  action = null,
  externalQueries,
  onQueriesChange
}: ModularSearchWidgetProps) => {
  const {
    isCurrent : isCurrentTheme,
    isLegacy  : isLegacyTheme
  } = useThemeContext();

  const classes = useStyles({
    isPhone,
    isCurrentTheme,
    isGridInsideADialog,
    isMobileModularSearch
  });

  const isMounted                                    = useHasMounted();
  const latestQueriesForPage                         = getSavedQueriesForPage(pageName);
  const queryInputRef                                = useRef(null);
  const menuListRef                                  = useRef(null);
  const hasSavedQuery                                = useRef(null);
  const [queryAttribute, setQueryAttribute]          = useState<string>(searchInstance?.getFirstAttributeLabel());
  const [searchQuery, updateSearchQuery]             = useState<SearchQuery>(INITIAL_SEARCH_QUERY);
  const [menuAutoFocus, updateMenuAutoFocus]         = useState<boolean>(false);
  const [isFiltersSaved, setIsFiltersSaved]          = useState<boolean>(false);
  const [isSearchError, setIsSearchError]            = useState<boolean>(false);
  const [isSaveRequired, setIsSaveRequired]          = useState<boolean>(false);
  const [canShowDeletePrompt, onCanShowDeletePrompt] = useState<string | null>(null);
  const [filterLookUpData, setFilterLookUpData]      = useState<LookUpData[]>(null);
  // The latest lookup state for fields with their own `resolveLookupQuery`, read
  // on Enter so they never resolve against options from before they loaded
  const lookupStateRef                               = useRef({filterLookUpData, isCaseInsensitive, searchInstance});
  lookupStateRef.current                             = {filterLookUpData, isCaseInsensitive, searchInstance};
  const [showSuggestions, setShowSuggestions]        = useState(false);
  const [internalQueries, setInternalQueries]        = useState<QueryChipData[]>([]);
  const isControlled                                 = externalQueries !== undefined;
  const queries                                      = isControlled ? externalQueries : internalQueries;

  const [savedQueryName, setSavedQueryName] = useState<string | null>(
    latestQueriesForPage?.queryName !== SEARCH_NAME ? latestQueriesForPage?.queryName : null
  );

  const [
    suggestionDropDown,
    updateSuggestionsDropdown
  ] = useState<Dropdown>({
    open       : null,
    type       : SuggestionsType.TEXT,
    sourceType : null
  });

  const searchInputPlaceholder = `${inputPlaceholder} by ${searchInstance?.getLabel(queryAttribute)}`;
  const mappings               = searchInstance?.mappings[queryAttribute];

  const limit    = mappings?.hasLargeMaxLimit ? IDENTIFIER_MAX_IDENTIFIER_SEARCH_LIMIT
    : mappings?.hasIDMaxLimit ? PRIMARY_ID_SEARCH_LIMIT : MAX_IDENTIFIER_SEARCH_LIMIT;

  const defaultSearchErrMsg = translate('common:search.globalSearchLimitError', {limit});

  const [shouldSaveQueryOnEnter, setShouldSaveQueryOnEnter] = useState(false);
  const [searchErrMsg, setSearchErrMsg]                     = useState<string>(defaultSearchErrMsg);

  const isGroupSuggestion = suggestionDropDown?.sourceType === LookUpSource.Group;
  const isRoleSuggestion  = suggestionDropDown?.sourceType === LookUpSource.Role;

  const {
    canShowQueryOperations
  } = useQueryDialog(queries);

  const updateQueries = useCallback((newQueries: QueryChipData[]) => {
    if (isControlled) {
      onQueriesChange?.(newQueries);
    } else {
      setInternalQueries(newQueries);
    }
  }, [isControlled, onQueriesChange]);

  /**
   * Function to open suggestions dropdown
   * @private
   */
  function setSuggestionOpen(element, type, sourceType) {
    updateSuggestionsDropdown({open: element, type, sourceType});
  }

  /**
   * @private
   * Utility function to remove all queries saved in user preference.
   */
  async function resetSavedQueriesForPage() {
    const userPreferences = UserPreferences.get(SEARCH_QUERIES_PREFERENCE_NAME) ?? [];
    const otherPages: SavedQueryType[] =
      userPreferences?.filter(({pageName: name}: SavedQueryType) => name !== pageName);
    await UserPreferences.set(SEARCH_QUERIES_PREFERENCE_NAME, [...otherPages], !UserPreferences.hasChangeDetection());
  }

  /**
   * @private
   * Utility function to remove the exact or partial queries saved in user preference.
   */
  async function removeExactOrPartialQueries(attribute: string = null) {
    const userPreferences  = UserPreferences.get(SEARCH_QUERIES_PREFERENCE_NAME) ?? [];
    const otherPageQueries = userPreferences?.filter?.(({pageName: name}: SavedQueryType) => name !== pageName);
    const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
    const filteredQueries = filterOutExactOrPartialQueries(attribute, savedQueriesForPage);
    savedQueriesForPage.query = filteredQueries;
    await UserPreferences.set(
      SEARCH_QUERIES_PREFERENCE_NAME,
      [...otherPageQueries, savedQueriesForPage],
      !UserPreferences.hasChangeDetection()
    );
  };

  /**
   * @private
   * Utility function to check if Exact related attribute i.e. `firmwareVersion` or `configVersion`
   * present in Search query in user preference.
   */
  function hasExactAttributeInSearchQuery(queries: SavedQueryItem[], queryName = SEARCH_NAME): boolean {
    const presentItem = queries?.find?.(({name}) => name === queryName);
    if (!presentItem) return false;
    const exactAttributes = [
      SearchScopeValues.FIRMWARE_VERSION,
      SearchScopeValues.CONFIG_VERSION,
      SearchScopeValues.APP_ID,
      SearchScopeValues.DEVICE_NAME,
      SearchScopeValues.ESN,
      SearchScopeValues.SERIAL_NUMBER,
      SearchScopeValues.IMEI,
      SearchScopeValues.IMEI2,
      SearchScopeValues.AIR_ID,
      SearchScopeValues.ICCID,
      SearchScopeValues.MEID,
      SearchScopeValues.MDT,
      SearchScopeValues.LMU,
      SearchScopeValues.USER_ESN_MOBILE_ID,
      SearchScopeValues.ANDROID_OS_VERSION,
      SearchScopeValues.FILE_VERSION
    ];
    try {
      const parsedQuery = safeJSONParse(presentItem?.query);
      if (!Array.isArray(parsedQuery)) return false;
      return parsedQuery?.some?.(({attribute}: {attribute?: SearchScopeValues}) =>
        attribute && exactAttributes?.includes?.(attribute)
      );
    } catch {
      return false;
    }
  };

  async function removeEmptyQueries() {
    const userPreferences = UserPreferences.get(SEARCH_QUERIES_PREFERENCE_NAME) ?? [];
    const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
    const savedQueriesLength = savedQueriesForPage?.query?.length;
    if (savedQueriesLength) {
      const filteredQueriesForPage = savedQueriesForPage.query?.filter(
        ({query}: SavedQueryItem) => query && (safeJSONParse(query)?.length || query?.length));
      const filteredQueriesLength = filteredQueriesForPage?.length;
      if (savedQueriesLength > filteredQueriesLength) {
        const otherPageQueries = userPreferences?.filter(({pageName: name}: SavedQueryType) => name !== pageName);
        if (filteredQueriesLength) {
          savedQueriesForPage.query = filteredQueriesForPage;
          await UserPreferences.set(
            SEARCH_QUERIES_PREFERENCE_NAME,
            [...otherPageQueries, savedQueriesForPage],
            !UserPreferences.hasChangeDetection()
          );
        } else {
          await UserPreferences.set(
            SEARCH_QUERIES_PREFERENCE_NAME,
            [...otherPageQueries],
            !UserPreferences.hasChangeDetection()
          );
        }
      }
    }
  }

  const getDefaultQuery = useCallback(() => {
    const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
    if (!isEmpty(savedQueriesForPage)) {
      const selectedQuery = savedQueriesForPage?.query?.filter(
        ({name}: SavedQueryItem) => name === savedQueriesForPage?.queryName)?.[0] ?? {};
      return selectedQuery as SavedQueryItem;
    }
    return {} as SavedQueryItem;
  }, [pageName]);

  const hasChangeInSearch = useCallback(() => {
    if (queries?.length) {
      if (savedQueryName) {
        const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
        const savedQuery = savedQueriesForPage.query.filter(({name}) => name === savedQueryName)?.[0];
        return safeJSONStringify(queries) !== savedQuery?.query;
      }
      return true;
    }
    return false;
  }, [pageName, queries, savedQueryName]);

  useEffect(() => {
    if (!isNull(queries) && shouldSaveQueryOnEnter) {
      if (hasChangeInSearch()) {
        saveQuery(SEARCH_NAME, undefined, false);
      }
      setShouldSaveQueryOnEnter(false);
    }
  }, [queries, shouldSaveQueryOnEnter]);

  // using default search from UserPreferences if exists
  useEffect(() => {
    removeEmptyQueries();
    const defaultQuery = getDefaultQuery();
    if (!isEmpty(defaultQuery)) {
      onSavedQuerySelect(defaultQuery);
    } else {
      updateQueries([]);
    }
  }, []);

  //setting default query for Manufactured Devices
  useEffect(() => {
    const lastOneMonth = moment().subtract(1, 'months').format('MM-DD-YYYY');
    if ((pageName === PageName.MFG_DEVICES) && (queries?.length === 0)) {
      updateQueries([{
        label     : translate('common:modularsearch.date.lastOneMonth'),
        attribute : 'createdOn',
        value     : getSearchableDate(lastOneMonth)
      }]);
    }
  }, [queries, pageName]);

  // when change in saved query name, save in to User Preferences
  useEffect(() => {
    const saveDefaultQueryName = async() => {
      const userPreferences = UserPreferences.get(SEARCH_QUERIES_PREFERENCE_NAME) ?? [];
      const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
      if (!isEmpty(savedQueriesForPage) && (savedQueriesForPage.queryName !== savedQueryName || !queries?.length)) {
        if (queries?.length) {
          savedQueriesForPage.queryName = savedQueryName;
        }
        const otherPageQueries = userPreferences?.filter(({pageName: name}: SavedQueryType) => name !== pageName);
        await UserPreferences.set(
          SEARCH_QUERIES_PREFERENCE_NAME,
          [...otherPageQueries, savedQueriesForPage],
          !UserPreferences.hasChangeDetection()
        );
      }
    };

    return () => {
      if (!hasChangeInSearch()) {
        saveDefaultQueryName();
      }
    };
  }, [pageName, queries, savedQueryName]);

  // when change in search, save in to User Preferences
  useEffect(() => {
    if (isSaveRequired) {
      saveQuery(savedQueryName ?? SEARCH_NAME, undefined, false);
    }
  }, [pageName, queries, savedQueryName, isSaveRequired]);

  useEffect(function() {
    if (isFiltersSaved) {
      const timeoutRef = setTimeout(function() {
        setIsFiltersSaved(false);
        clearTimeout(timeoutRef);
      }, 3000);
    }
  }, [isFiltersSaved]);

  useEffect(function() {
    if (isSaveRequired) {
      const timeoutVal = setTimeout(function() {
        setIsSaveRequired(false);
        clearTimeout(timeoutVal);
      }, 3000);
    }
  }, [isSaveRequired]);

  useEffect(() => {
    if (isMounted) {
      const hasExactMatch = hasFilteredExactMatch({
        queries,
        pageName,
        hasSavedQuery : hasSavedQuery.current
      });
      getSearchParams(searchInstance?.transformQueriesToRequestParams(queries, hasExactMatch));
      getIdentifierParams?.(hasSavedQuery.current);
      if (queryAttribute === SAVED_QUERIES_VALUE) {
        const firstAvailableQuery = searchInstance?.getFirstAttributeLabel(queries, autoSelectNext);
        setQueryAttribute(firstAvailableQuery);
      }
    }
    getFilterQueries?.(queries);
  }, [queries, pageName]);

  useEffect(() => {
    if (queryAttribute) {
      if (queryAttribute !== SAVED_QUERIES_VALUE) {
        const {
          type
        } = searchInstance?.getMappingObject(queryAttribute);
        if (type === AttributeTypes.TEXT) {
          setSuggestionOpen(false, null, null);
        }
      } else {
        setSuggestionOpen(false, null, null);
      }
    }
  }, [queryAttribute]);

  /**
   * Function to cleanup suggestion dropdown
   * @private
   */
  const handleClose = useCallback(function() {
    updateSuggestionsDropdown({
      open       : suggestionDropDown.type === SuggestionsType.DATE,
      type       : suggestionDropDown.type,
      sourceType : suggestionDropDown.sourceType
    });
    if (!isEqual(queryAttribute, 'progressStatus')) {
      updateSearchQuery(INITIAL_SEARCH_QUERY);
    }
    if (suggestionDropDown.type !== SuggestionsType.DATE) {
      setShowSuggestions(false);
    }
  }, [suggestionDropDown, setShowSuggestions, queryAttribute]);

  const onSelectAttribute = useCallback(function(event, fromSource = false) {
    const {target: {value, dataset}} = event;
    const item = (value === 0 ? dataset?.value : value) || queryAttribute;
    setQueryAttribute(item);
    if (item !== 'saved') {
      updateSearchQuery(INITIAL_SEARCH_QUERY);
      const timeoutRef = setTimeout(function() {
        fromSource && onKeyDown(new KeyboardEvent('keypress', {key: 'ArrowDown', which: 13}), item);
        queryInputRef?.current?.focus();
        clearTimeout(timeoutRef);
      }, 300);
    }
  }, [queryInputRef, suggestionDropDown]);

  /**
   * Function to track input element and dropdown keydown
   * @private
   */
  const onKeyDown = useCallback(function(event, attribute = null) {
    event.stopPropagation();
    if (!isSearchError) {
      if (isDownArrow(event)) {
        event.preventDefault();
        const attributeForQuery = attribute ?? queryAttribute;
        const mapping = searchInstance?.getMappingObject(attributeForQuery);
        if (attributeForQuery?.trim() !== SAVED_QUERIES_VALUE && mapping.type !== AttributeTypes.TEXT) {
          updateSuggestionsDropdown({
            type       : mapping?.suggestion,
            sourceType : mapping?.suggestionsSource,
            open       : true
          });
          if (event?.target?.id === SEARCH_INPUT_ID) {
            menuListRef.current?.children[0]?.focus();
          }
        }
      } else if (isEnter(event) && !isGroupSuggestion && !isRoleSuggestion) {
        let record = null;
        if (isAnySuggestionType(SuggestionsType.LOOKUP)(suggestionDropDown.type) && !searchQuery.option) {
          const latest             = lookupStateRef.current;
          const resolveLookupQuery = latest.searchInstance?.getMappingObject(queryAttribute)?.resolveLookupQuery;
          if (resolveLookupQuery) {
            const shouldSubmit = resolveLookupQuery(searchQuery, latest.filterLookUpData, latest.isCaseInsensitive);
            record             = shouldSubmit ? searchQuery : null;
          } else {
            record = filterDynamicDataList(filterLookUpData, searchQuery?.query, -1, 'name', isCaseInsensitive);
            if (record.length === 1) {
              searchQuery.option = record[0];
            }
            record = record.length > 1 ? null : record;
          }
        } else {
          record = searchQuery;
        }

        if (record) {
          onEnter(searchQuery);
          updateSuggestionsDropdown({open: null, type: suggestionDropDown.type, sourceType: null});
        }
      } else if (isTab(event)) {
        updateSuggestionsDropdown({open: null, type: suggestionDropDown.type, sourceType: null});
      }
    } else {
      setIsSearchError(false);
    }
  }, [queries, searchQuery, suggestionDropDown, queryAttribute]);

  // Submits the given search query as a chip.
  const onEnter = useCallback(function(searchQuery: SearchQuery) {
    if (searchQuery?.query?.length) {
      if (queryAttribute?.toLowerCase() === 'any' && !isValidLogicalSearch(searchQuery)) {
        setIsSearchError(true);
        setSearchErrMsg('Invalid Logical search provided.');
        return false;
      }
      const compactSearchQuery = removeEndSeperators(searchQuery?.query, isValidOperation(queryAttribute));
      const queriesData = searchInstance
        ?.createQueryChips({
          attribute   : queryAttribute,
          queryOption : {
            ...searchQuery,
            query : compactSearchQuery
          }
        });
      const doesQueryExist = queries?.findIndex((query: QueryChipData) => query.attribute === queriesData.attribute);
      if (queriesData) {
        queriesData.queryType = getQueryTypeForAttribute(queryAttribute, latestQueriesForPage) ||
          (isExactSearchScope(latestQueriesForPage, queryAttribute, pageName) ? SearchScopeValues.EXACT : '');
        let combinedQuery = [...queries];
        if (doesQueryExist === -1) {
          combinedQuery = [...combinedQuery, queriesData];
        } else {
          combinedQuery[doesQueryExist] = queriesData;
        }
        const isExactSearch = hasFilteredExactMatch({
          queries : latestQueriesForPage,
          isEnter : true,
          pageName,
          queryAttribute
        });
        if (isEqual(queryAttribute, 'progressStatus')) {
          getSearchParams(searchInstance?.transformQueriesToRequestParams(combinedQuery));
        } if (isQueryAttributeValid(queryAttribute, pageName) && isExactSearch) {
          getSearchParams(searchInstance?.transformQueriesToRequestParams(combinedQuery, isExactSearch));
          combinedQuery?.forEach(query => {
            if (isExactSearch?.[query.attribute as SearchScopeValues]) {
              query.exactFields = {
                ...query.exactFields,
                [query.attribute] : true
              };
            }
          });
        }
        updateQueries(combinedQuery);
        updateMenuAutoFocus(false);
        if (!isEqual(queryAttribute, 'progressStatus')) {
          updateSearchQuery(INITIAL_SEARCH_QUERY);
          setSuggestionOpen(false, queryInputRef.current, null);
          if (autoSelectNext) {
            const firstQuery = searchInstance?.getFirstAttributeLabel(combinedQuery, autoSelectNext);
            setQueryAttribute(firstQuery);
          }
        }
        if (!isNull(combinedQuery)) {
          setShouldSaveQueryOnEnter(true);
        }
      }
    }
    setIsSearchError(false);
  }, [queryAttribute, searchQuery, queries, savedQueryName, latestQueriesForPage]);

  /**
   * When user selects an options from multi-select and presses tab,
   * it will trigger onKeyDown event with Enter key.
   */
  const inputEventTrigger = useCallback(function(shouldSelect: boolean) {
    queryInputRef?.current.focus();
    if (shouldSelect) {
      onKeyDown(new KeyboardEvent('keypress', {key: 'Enter', which: 13}));
    }
  }, [queries, searchQuery, queryAttribute]);

  const onInputChange = useCallback(({target: {value}}: ChangeEvent<HTMLInputElement>) => {
    const mapping = searchInstance?.mappings[queryAttribute];
    setIsSearchError(isIdentifierLimitExceed({
      queryValue       : value,
      hasLargeMaxLimit : mapping?.hasLargeMaxLimit,
      hasIDMaxLimit    : mapping?.hasIDMaxLimit,
      hasAnyField      : mapping?.hasAnyField
    }));
    setSearchErrMsg(defaultSearchErrMsg);
    updateSearchQuery({
      query  : mapping.hasNumericValue ? ((mapping.hasMultipleNumericValue ? onlyMultipleNumbers(value) : onlyNumbers(value)) || '').toString() : value,
      option : null
    });
    if (debouncedInputSelectionMap[mapping?.key]) {
      debouncedHandleInput(value, mapping);
    } else {
      setShowSuggestions(true);
      if (isAnySuggestionType(SuggestionsType.LOOKUP)(mapping.suggestion)) {
        onKeyDown(new KeyboardEvent('keypress', {key: 'ArrowDown', which: 13}), queryAttribute);
      } else if (!isAnySuggestionType(SuggestionsType.TEXT)(mapping.suggestion)) {
        setSuggestionOpen(true, mapping.suggestion, suggestionDropDown.sourceType);
      }
    }
  }, [queryAttribute, updateSearchQuery, setIsSearchError, searchInstance]);

  const debouncedHandleInput = useCallback(debounce((value: string, mapping) => {
    if (value?.length < 1) {
      setShowSuggestions(false);
    }
    if (value?.length >= 2) {
      setShowSuggestions(true);
      updateSuggestionsDropdown({
        open       : true,
        type       : mapping.suggestion,
        sourceType : mapping.suggestionsSource
      });
    }
  }, 500), [setShowSuggestions]);

  /**
   * @param menuItem contains SEARCH_LABEL SEARCH_CONDITION. e.g: NAME CONTAINS
   * @param source specifies where the selection is from
   */
  const onMenuSelection = useCallback(function(
    menuItem: MenuItem,
    source: SuggestionsType,
    event?: KeyboardEvent | {canSubmit: boolean}
  ) {
    const selection = searchInstance?.getSelectedQuery({
      menuItem,
      source,
      attribute : queryAttribute,
      query     : searchQuery.query
    });
    if (isSearchQueryValueType(menuItem)) {
      menuItem.displayTitle =
        searchInstance?.getDisplayableQueryAttribute(queryAttribute, menuItem);
    }
    const queryToUpdate = {
      query  : selection,
      option : isAnySuggestionType(SuggestionsType.LOOKUP, SuggestionsType.DATE)(source) ? menuItem : null
    };
    updateSearchQuery(queryToUpdate);

    if ((BaseSearchMapping.isMultiSelectionType && event?.type === 'click') && selection) {
      onEnter(queryToUpdate);
    }

    if ((BaseSearchMapping.isSelectionType(source) ||
      (BaseSearchMapping.isDateSelection(source) && event?.canSubmit)) && selection) {
      onEnter(queryToUpdate);
    }
  }, [searchQuery, queryAttribute]);

  /**
   * Function to update state lookup data.
   * @private
   */
  const updateFilterLookUpData = useCallback((data) => {
    setFilterLookUpData(data);
  }, []);

  /**
   * Function to remove the query chip.
   * @private
   */
  const onQueryRemove = useCallback(function(attribute: string) {
    return function() {
      const filteredQueries = queries.filter((queryChip: QueryChipData) => queryChip.attribute !== attribute);
      removeExactOrPartialQueries(attribute);
      updateQueries([...filteredQueries]);
      setQueryAttribute(searchInstance?.getFirstAttributeLabel(filteredQueries, autoSelectNext));
      updateSearchQuery(INITIAL_SEARCH_QUERY);
      if (isEmpty(filteredQueries)) {
        setSavedQueryName(null);
        setIsSaveRequired(true);
      } else {
        setShouldSaveQueryOnEnter(true);
      }
    };
  }, [queries]);

  const saveQuery = useCallback(async function(savedName: string, callback, showMessage = true) {
    const name         = savedName?.length > 0 ? savedName : savedQueryName;
    const savedQueries = UserPreferences.get(SEARCH_QUERIES_PREFERENCE_NAME) ?? [];
    const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
    if (queries.length > 0) {
      if (isEmpty(savedQueriesForPage)) {
        savedQueriesForPage.pageName  = pageName;
        savedQueriesForPage.createdAt = new Date();
        savedQueriesForPage.query     = [];
        savedQueriesForPage.queryName = '';
      } else if (name) {
        savedQueriesForPage.queryName = name;
      }
      if (!showMessage) {
        savedQueriesForPage.queryName = name;
      }
      const savedQueryIndex = savedQueriesForPage
        .query
        .findIndex(({name: savedQueryName}) => name === savedQueryName);
      if (hasDateField) {
        queries?.forEach(query => {
          const {attribute, value} = query || {};
          if (DateAttributes.indexOf(attribute) !== -1 && DateLabels.indexOf(value?.label) !== -1) {
            if (Object?.getOwnPropertyDescriptor(query?.value, 'value')?.writable) {
              value.value = getDateSelection(value?.label);
            }
          }
        });
      }
      if (savedQueryIndex >= 0) {
        savedQueriesForPage.query[savedQueryIndex].query = safeJSONStringify(queries);
      } else {
        savedQueriesForPage.query.push({
          name,
          query : safeJSONStringify(queries)
        });
        callback?.();
      }
      const modularSavedQueries = savedQueries?.filter(
        (page: SavedQueryType) => page.pageName !== pageName);
      await UserPreferences.set(
        SEARCH_QUERIES_PREFERENCE_NAME,
        [...modularSavedQueries, savedQueriesForPage],
        !UserPreferences.hasChangeDetection()
      );
      if (name !== SEARCH_NAME) {
        setSavedQueryName(name);
      }
      if (showMessage) {
        setIsFiltersSaved(true);
      }
    } else if (queries.length === 0 && savedQueriesForPage.queryName) {
      savedQueriesForPage.queryName = null;
      const modularSavedQueries = savedQueries?.filter(
        (page: SavedQueryType) => page.pageName !== pageName);
      await UserPreferences.set(
        SEARCH_QUERIES_PREFERENCE_NAME,
        [...modularSavedQueries, savedQueriesForPage],
        !UserPreferences.hasChangeDetection()
      );
    }
  }, [queries, pageName]);

  const onSavedQuerySelect = useCallback(function({name, query}: SavedQueryItem) {
    if (name) {
      if (name !== SEARCH_NAME) {
        setSavedQueryName(name);
      }
      const selectedQuery = safeJSONParse(query);
      updateQueries(selectedQuery);
      setIsSaveRequired(true);
    }
    hasSavedQuery.current = name;
  }, []);

  const onConfirmRemove = useCallback(function(savedQueryName: string) {
    onCanShowDeletePrompt(savedQueryName);
  }, []);

  const onRemoveQuery = useCallback(async function(savedQueryName: string) {
    const savedQueries = UserPreferences.get(SEARCH_QUERIES_PREFERENCE_NAME) ?? [];
    const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
    if (!isEmpty(savedQueriesForPage)) {
      const updatedQueries = savedQueriesForPage?.query?.filter?.(({query, name}: SavedQueryItem) => {
        const isExactOrPartial = query === SearchScopeValues.EXACT || query === SearchScopeValues.PARTIAL;
        if (isExactOrPartial) {
          return name !== savedQueryName && !isExactOrPartial;
        } else {
          return name !== savedQueryName;
        }
      });
      savedQueriesForPage.query = updatedQueries;
      await UserPreferences.set(
        SEARCH_QUERIES_PREFERENCE_NAME,
        [...savedQueries, savedQueriesForPage],
        !UserPreferences.hasChangeDetection()
      );
      setSavedQueryName(null);
      updateQueries([]);
      setIsSaveRequired(true);
    }
  }, [pageName]);

  const onCloseMessage = useCallback(function() {
    setIsFiltersSaved(false);
  }, []);

  const onClearAll = useCallback(function() {
    setSavedQueryName(null);
    updateQueries([]);
    setIsSaveRequired(true);
    removeExactOrPartialQueries();
    updateSearchQuery(INITIAL_SEARCH_QUERY);
  }, [savedQueryName]);

  const onDeleteConfirm = useCallback(function() {
    onRemoveQuery(canShowDeletePrompt);
    onCanShowDeletePrompt(null);
  }, [canShowDeletePrompt]);

  const onDeleteCancel = useCallback(function() {
    onCanShowDeletePrompt(null);
  }, []);

  const deletePromptOverride = useCallback(() =>
    translate('common:modularsearch.deleteConfirmationMessage', {
      name : canShowDeletePrompt
    }), [canShowDeletePrompt]);

  const onUndo = useCallback(async function(queryName) {
    const savedQueriesForPage: SavedQueryType = getSavedQueriesForPage(pageName);
    const selectedQuery = savedQueriesForPage?.query?.filter(
      ({name}: SavedQueryItem) => name === queryName)?.[0];
    const parsedQuery = !isEmpty(selectedQuery) ? safeJSONParse(selectedQuery?.query) : [];
    if (hasExactAttributeInSearchQuery(savedQueriesForPage?.query)) {
      removeExactOrPartialQueries();
    };
    updateQueries(parsedQuery);
    setIsSaveRequired(true);
  }, [pageName, queries]);

  const isValidOperation = (queryName): boolean => {
    if (isEqual(copyPasteQueryNames.ESN, queryName) || isEqual(copyPasteQueryNames.APPID, queryName) ||
    isEqual(copyPasteQueryNames.SERIALNUMBER, queryName) || isEqual(copyPasteQueryNames.ANY, queryName)) {
      if (isEqual(copyPasteQueryNames.DEVICES, pageName)) {
        return true;
      }
    }
    return false;
  };

  const getDynamicChipValue = useCallback((queryChipData, value) => {
    if (isEqual(queryChipData?.attribute, VBUS_STATUS)) {
      if (isEqual(value, FALSE)) {
        return VBUS_STATUS_OFF;
      } if (isEqual(value, TRUE)) {
        return VBUS_STATUS_ON;
      }
    } else if (isEqual(queryChipData?.attribute, IS_THIRD_PARTY)) {
      if (isEqual(value, FALSE)) {
        return IS_THIRD_PARTY_NO;
      } if (isEqual(value, TRUE)) {
        return IS_THIRD_PARTY_YES;
      }
    }
    return '';
  }, []);

  const getChipValue = (attribute: string, dynamicValue: string, value: string) => ({
    [VBUS_STATUS]    : dynamicValue,
    [IS_THIRD_PARTY] : dynamicValue
  }[attribute] ?? value);

  const onClickQueryChip = useCallback(function(queryChipData) {
    const {attribute, value} = queryChipData;
    const dynamicChipValue   = getDynamicChipValue(queryChipData, value);
    const chipValue          = getChipValue(queryChipData?.attribute, dynamicChipValue, value);
    // A chip built from a predefined option holds the value the search filters on,
    // so it is translated back to the option's label before repopulating the field.
    const optionLabel        = searchInstance?.getOptionLabel(attribute, chipValue) ?? chipValue;
    const searchValue        = getSelectedChipValue(optionLabel, attribute);
    const mapping            = searchInstance.getMappingObject(attribute);
    if (mapping) {
      updateSearchQuery(searchValue);
      setQueryAttribute(attribute);
      updateSuggestionsDropdown({
        open       : mapping.suggestion !== SuggestionsType.TEXT,
        type       : mapping.suggestion,
        sourceType : mapping.suggestionsSource
      });
    }
  }, []);

  const queryChips = (
    <DisplayQueryChips queries={queries}
      isPhone={isPhone}
      isGridInsideADialog={isGridInsideADialog}
      pageName={pageName}
      onUndo={onUndo}
      onClearAll={onClearAll}
      queryName={savedQueryName}
      onQueryRemove={onQueryRemove}
      saveQuery={saveQuery}
      adornmentClass={classes.searchIcon}
      onClickQueryChip={onClickQueryChip}
      onRemoveQuery={onConfirmRemove}
      onSavedQuerySelect={onSavedQuerySelect}
      showFullWidthSearch={showFullWidthSearch} />
  );

  if (isMounted) {
    return (
      <Hidden show={!isMobileModularSearch ? 'desktop' : ''}>
        <ClickAwayListener onClickAway={handleClose}>
          {searchInstance && (
            // ClickAwayListener clones this element and attaches its own ref
            // to it (to know what counts as "inside") - a bare Fragment
            // can't receive a ref, so that ref silently never attached to
            // any real DOM node and onClickAway never fired. A real div
            // gives it something to actually attach to (clickAwayWrapper
            // keeps legacy's box model exactly as it was via
            // display:contents - see that class) ...
            <div className={classes.clickAwayWrapper}>
              <Render.If condition={isCurrentTheme && canShowQueryOperations}>
                <DisplayQueryChipsLayout queries={queries}
                  onClearAll={onClearAll}
                  onQueryRemove={onQueryRemove}
                  onClickQueryChip={onClickQueryChip} />
              </Render.If>
              <div className={showFullWidthSearch ? classes.widgetWrapperFullWidth : classes.widgetWrapper}>
                <div className={!isPhone ? classes.searchWrapper : classes.phoneSearchWrapper}>
                  <Hidden show='desktop'>
                    <AttributeSuggestions onSelection={function(e) {
                      onSelectAttribute(e, true);
                    }}
                    value={queryAttribute}
                    classes={classes}
                    mapping={searchInstance.mappings}
                    listOptions={searchInstance.getSearchableOptions(
                      searchQuery.query,
                      SuggestionsType.ATTRIBUTE,
                      queries
                    )} />
                  </Hidden>
                  <div id={SEARCH_WRAPPER}
                    className={showFullWidthSearch ? classes.searchSuggestionInputWrapperFullWidth
                      : classes.searchSuggestionInputWrapper}>
                    <ConditionalRender condition={isGroupSuggestion}>
                      <GroupSearchInput placeholder={searchInputPlaceholder}
                        onMenuSelection={onMenuSelection}
                        onQueryRemove={onQueryRemove}
                        viewSubGroups={viewSubGroups}
                        accountTree={accountTree}
                        accountId={accountId}
                        queries={queries} />
                    </ConditionalRender>
                    <Render.If condition={isRoleSuggestion}>
                      <RoleSearchInput placeholder={searchInputPlaceholder}
                        onMenuSelection={onMenuSelection}
                        viewSubGroups={viewSubGroups}
                        accountTree={accountTree}
                        accountId={accountId} />
                    </Render.If>
                    <ConditionalRender condition={!isGroupSuggestion && !isRoleSuggestion}>
                      <TextField id={SEARCH_INPUT_ID}
                        classes={{root: classes.searchSuggestionInputRoot}}
                        autoComplete={'off'}
                        placeholder={searchInputPlaceholder}
                        helperText={isGroupSuggestion && isRoleSuggestion && translate('common:modularsearch.groupsHelperText')}
                        inputRef={queryInputRef}
                        onClick={onInputChange}
                        onChange={onInputChange}
                        onKeyDown={onKeyDown}
                        value={searchQuery.query}
                        hasError={isSearchError}
                        errorText={searchErrMsg}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        inputProps={{
                          maxLength : searchInstance.getSelectionCharacterLength(queryAttribute)
                        }}
                        InputProps={{
                          classes : {
                            input     : classes.input,
                            underline : classes.searchSuggestionInputUnderline
                          },
                          startAdornment : (<div className={classes.searchIcon}>
                            <TopNavSearchIcon />
                          </div>),
                          endAdornment : (
                            <>
                              <Render.If condition={!isNil(action)}>
                                {action}
                              </Render.If>
                              <Render.If condition={isQueryAndPageValid(queryAttribute, pageName)}>
                                <div className={classes.exactContainer}>
                                  <SearchScope queryAttribute={queryAttribute}
                                    hasMultipleValues={hasWhitespaceOrComma(searchQuery.query)}
                                    pageName={pageName} />
                                </div>
                              </Render.If>
                              <Render.If condition={mappings?.hasToolTip}>
                                <Tooltip arrow title={getMappingToolTip(mappings)}>
                                  <span>
                                    <InfoIcon />
                                  </span>
                                </Tooltip>
                              </Render.If>
                              <div className={classes.copyPasteIcon}>
                                <ConditionalRender condition={isValidOperation(queryAttribute)}>
                                  <SearchFieldTooltip queryName={queryAttribute}
                                    searchInstance={searchInstance}
                                    limit={limit} />
                                </ConditionalRender>
                              </div>
                            </>
                          )
                        }}/>
                    </ConditionalRender>
                    <Popper open={Boolean(suggestionDropDown.open)}
                      className={classes.suggestionsWrapper}
                      anchorEl={suggestionDropDown.open ? queryInputRef.current : null}
                      role={undefined}
                      transition
                      disablePortal
                      style={{}}>
                      <Paper className={classes.suggestionsPaper}>
                        {Boolean(suggestionDropDown.open) && showSuggestions && (
                          <SearchSuggestions menuListRef={menuListRef}
                            inputValue={searchQuery.query}
                            inputEventTrigger={inputEventTrigger}
                            closeDropdown={() => {}}
                            isCaseInsensitive={isCaseInsensitive}
                            hasDateField={hasDateField}
                            selectedSuggestions={searchInstance.getSelectedSuggestion(searchQuery.query)}
                            query={searchInstance.getValue(searchQuery.query)}
                            menuAutoFocus={menuAutoFocus}
                            onMenuSelection={onMenuSelection}
                            menuSelectionType={suggestionDropDown.type}
                            attributeMappings={searchInstance.mappings}
                            lookUp={!isNull(suggestionDropDown.sourceType)
                              ? searchInstance.getCurrentLookup(suggestionDropDown.sourceType)
                              : null
                            }
                            updateFilterLookUpData={updateFilterLookUpData}
                            listOptions={searchInstance.getSearchableOptions(
                              queryAttribute,
                              suggestionDropDown.type,
                              queries
                            )}
                            accountTree={accountTree}
                            setShowSuggestions={setShowSuggestions} />
                        )}
                      </Paper>
                    </Popper>
                  </div>
                  <Render.If condition={isCurrentTheme}>
                    {queryChips}
                  </Render.If>
                </div>
                <Render.If condition={isLegacyTheme}>
                  {queryChips}
                </Render.If>
              </div>
            </div>
          )}
        </ClickAwayListener>
        <ConditionalRender condition={isGroupSuggestion && hasInheritableGroups}>
          <GroupInheritance checked={viewInheritableGroup}
            onChange={toggleInheritableGroupView}
            position={ViewSubGroupsPosition.GRID_CTAS} />
        </ConditionalRender>
        <IonSnackbar show={isFiltersSaved}
          message={translate('common:modularsearch.filtersSaved')}
          actionLabel={translate('common:editors.delete.ok')}
          onAction={onCloseMessage}
          onClose={onCloseMessage}
          autoHideDuration={3000} />
        {canShowDeletePrompt && (
          <DeletePrompt show
            entity={translate('common:modularsearch.savedSearch')}
            singleDeleteMessage={false}
            overrideMessage={deletePromptOverride}
            onConfirm={onDeleteConfirm}
            onCancel={onDeleteCancel}
            items={{}} />
        )}
      </Hidden>
    );
  }
  return null;
};

export default ModularSearchWidget;
